<template>
  <div class="h-100 d-flex flex-column">
    <div class="bg-dark p-1 d-flex">
      <options-form-field
        class="mb-0 flex-grow-1"
        :options="[
          { text: '50%', value: 0.5 },
          { text: '75%', value: 0.75 },
          { text: '100%', value: 1.0 },
          { text: '150%', value: 1.5 },
          { text: '200%', value: 2.0 }
        ]"
        v-model="scale"
        dropdown
      />
      <b-button variant="info" @click.prevent="downloadSvg($refs.svg)">
        <i class="fa fa-fw fa-download"></i> SVG
      </b-button>
      <!--<pre class="bg-danger p-3">{{ error }}</pre>-->
    </div>
    <div class="p-relative flex-grow-1">
      <div
        class="anchored scrollable text-center"
        tabindex="0"
        @keydown="onKeyPress"
        @copy="onCopy"
        @paste="onPaste"
      >
        <svg
          ref="svg"
          v-if="value"
          :width="diagramWidth * scale"
          :height="diagramHeight * scale"
          @mousedown="handleMouseDown"
        >
          <defs>
            <marker
              id="arrow"
              viewBox="0 0 10 10"
              refX="10"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>

          <g id="root-group" :transform="`scale(${scale} ${scale})`">
            <template v-for="n in nodes">
              <svg-rectangle
                :highlight="isSelected(n) || hasErrors(n)"
                :highlightStroke="hasErrors(n) ? errorColor : undefined"
                :key="n.id"
                :x="left(n)"
                :y="top(n)"
                :width="nodeWidth"
                :height="nodeHeight"
                :fill="fillFor(n)"
                :stroke="strokeFor(n)"
                :text="labelFor(n)"
              >
                <svg-icon
                  v-if="!noIcons && !n.deletable"
                  :value="fileIcon"
                  :x="-5"
                />
                <svg-icon
                  v-if="n.tagAlternatives.length > 0"
                  :value="tagsIcon"
                  :x="-5"
                  :y="-5"
                />
              </svg-rectangle>

              <template v-for="a in getOutgoingArrows(n)">
                <!-- Freccia dall'alto verso il basso -->
                <template
                  v-if="
                    n.colPosition === a.toStatus.colPosition &&
                      n.rowPosition < a.toStatus.rowPosition
                  "
                >
                  <svg-line
                    :highlight="shouldHighlight(n, a)"
                    :highlightStroke="hasErrors(a) ? errorColor : undefined"
                    :key="keyFor(n.id, a.toStatus.id)"
                    :x1="middleX(n) - lineShift"
                    :y1="bottom(n)"
                    :x2="middleX(a.toStatus) - lineShift"
                    :y2="top(a.toStatus)"
                    :text="a.label"
                    text-anchor="end"
                    marker-end="arrow"
                  />
                </template>
                <!-- Freccia dal basso verso l'alto -->
                <template
                  v-else-if="
                    n.colPosition === a.toStatus.colPosition &&
                      n.rowPosition > a.toStatus.rowPosition
                  "
                >
                  <svg-line
                    :highlight="shouldHighlight(n, a)"
                    :highlightStroke="hasErrors(a) ? errorColor : undefined"
                    :key="keyFor(n.id, a.toStatus.id)"
                    :x1="middleX(n) + lineShift"
                    :y1="top(n)"
                    :x2="middleX(a.toStatus) + lineShift"
                    :y2="bottom(a.toStatus)"
                    :text="a.label"
                    text-anchor="start"
                    marker-end="arrow"
                  />
                </template>
                <!-- Freccia da sinistra a destra -->
                <template
                  v-else-if="
                    n.colPosition < a.toStatus.colPosition &&
                      n.rowPosition === a.toStatus.rowPosition
                  "
                >
                  <svg-line
                    :highlight="shouldHighlight(n, a)"
                    :highlightStroke="hasErrors(a) ? errorColor : undefined"
                    :key="keyFor(n.id, a.toStatus.id)"
                    :x1="right(n)"
                    :y1="middleY(n) - lineShift"
                    :x2="left(a.toStatus)"
                    :y2="middleY(a.toStatus) - lineShift"
                    :text="a.label"
                    vertical-alignment="baseline"
                    marker-end="arrow"
                  />
                </template>
                <!-- Freccia da destra a sinistra -->
                <template
                  v-else-if="
                    n.colPosition > a.toStatus.colPosition &&
                      n.rowPosition === a.toStatus.rowPosition
                  "
                >
                  <svg-line
                    :highlight="shouldHighlight(n, a)"
                    :highlightStroke="hasErrors(a) ? errorColor : undefined"
                    :key="keyFor(n.id, a.toStatus.id)"
                    :x1="left(n)"
                    :y1="middleY(n) + lineShift"
                    :x2="right(a.toStatus)"
                    :y2="middleY(a.toStatus) + lineShift"
                    :text="a.label"
                    vertical-alignment="hanging"
                    marker-end="arrow"
                  />
                </template>
              </template>
            </template>
          </g>
        </svg>
      </div>
    </div>
  </div>
</template>
<script>
import HasErrorsMixin from "vue-template/common/mixins/HasErrorsMixin";
import orderBy from "lodash/orderBy";
import max from "lodash/max";
import findIndex from "lodash/findIndex";
import { findTarget, findComponent } from "core/utils";
import { downloadSvg } from "core/utils";
const fileIcon = require("!!vue-svg-loader!@fortawesome/fontawesome-free/svgs/solid/file.svg");
const tagsIcon = require("!!vue-svg-loader!@fortawesome/fontawesome-free/svgs/solid/tags.svg");

const SvgEventHandlerMixin = {
  props: {
    clickDelay: {
      type: Number,
      default: 400
    },

    selected: {
      type: Object,
      default: undefined
    },

    errorColor: {
      type: String,
      default: "orange"
    }
  },

  created() {
    this.mouseDownTimestamp = null;
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onCopy = this.onCopy.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  },

  methods: {
    downloadSvg(el) {
      downloadSvg(el);
    },

    onKeyPress(/*evt*/) {
      //console.log('KEY PRESS', evt);
    },

    onCopy(/*evt*/) {
      //console.log('COPY', evt);
    },

    onPaste(/*evt*/) {
      //console.log('PASTE', evt);
    },

    onMouseDown(/*evt*/) {
      //console.log('MOUSE DOWN', evt);
    },

    onMouseMove(/*evt*/) {
      //console.log('MOUSE MOVE', evt);
    },

    onMouseUp(/*evt*/) {
      //console.log('MOUSE UP', evt);
    },

    onClick(/*evt*/) {
      //console.log('CLICK', evt);
    },

    // =========================================================================

    handleMouseDown(evt) {
      this.mouseDownTimestamp = new Date().getTime();

      this.onMouseDown(evt);

      window.addEventListener("mousemove", this.handleMouseMove);
      window.addEventListener("mouseup", this.handleMouseUp);
    },

    handleMouseMove(evt) {
      if (!this.mouseDownTimestamp) {
        return;
      }

      const interval = new Date().getTime() - this.mouseDownTimestamp;

      if (interval > this.clickDelay) {
        this.onMouseMove(evt);
      }
    },

    handleMouseUp(evt) {
      if (!this.mouseDownTimestamp) {
        return;
      }

      const interval = new Date().getTime() - this.mouseDownTimestamp;

      if (interval <= this.clickDelay) {
        this.onClick(evt);
      } else {
        this.onMouseUp(evt);
      }

      this.mouseDownTimestamp = null;

      window.removeEventListener("mousemove", this.handleMouseMove);
      window.removeEventListener("mouseup", this.handleMouseUp);
    }
  }
};

export default {
  mixins: [SvgEventHandlerMixin, HasErrorsMixin],

  data() {
    return {
      scale: 1
    };
  },

  props: {
    noIcons: {
      type: Boolean,
      default: false
    },

    value: {
      type: Object
    },

    errors: {
      type: Array,
      default: () => []
    },

    padding: {
      type: Number,
      default: 50
    },

    nodeWidth: {
      type: Number,
      default: 250
    },

    nodeHeight: {
      type: Number,
      default: 100
    },

    hSpace: {
      type: Number,
      default: 300
    },

    vSpace: {
      type: Number,
      default: 100
    },

    lineShift: {
      type: Number,
      default: 20
    }
  },

  created() {
    this.fileIcon = fileIcon;
    this.tagsIcon = tagsIcon;
  },

  computed: {
    nodes() {
      return this.value
        ? orderBy(this.value.statuses, ["rowPosition", "colPosition"])
        : [];
    },

    initialNode() {
      return this.nodes.find(n => n.type === "INITIAL");
    },

    successNode() {
      return this.nodes.find(n => n.type === "SUCCESS");
    },

    failureNode() {
      return this.nodes.find(n => n.type === "FAILURE");
    },

    diagramWidth() {
      if (this.nodes.length < 1) {
        return this.padding * 2;
      }

      return max(this.nodes.map(n => this.right(n))) + this.padding;
    },

    diagramHeight() {
      if (this.nodes.length < 1) {
        return this.padding * 2;
      }

      return max(this.nodes.map(n => this.bottom(n))) + this.padding;
    }
  },

  methods: {
    isSelected(n) {
      return this.selected && this.selected.id === n.id;
    },

    shouldHighlight(n, a) {
      return (
        this.hasErrors(a) ||
        (this.selected &&
          this.keyFor(this.selected.fromStatusId, this.selected.toStatusId) ===
            this.keyFor(n.id, a.toStatus.id))
      );
    },

    hasErrors(n) {
      if (n.toStatus && n.fromStatus) {
        const nodeIndex = findIndex(
          this.value.statuses,
          s => s.id === n.fromStatus.id
        );
        const node = this.value.statuses[nodeIndex];
        const arcIndex = findIndex(
          node.outgoingArrows,
          a => a.toStatusId === n.toStatus.id
        );
        const errors = this.filterErrors(
          `statuses[${nodeIndex}].outgoingArrows[${arcIndex}]`
        );
        //console.log('nodeIndex', nodeIndex, 'arcIndex', arcIndex, 'errors', errors);
        return errors && errors.length > 0;
      } else {
        const index = findIndex(this.value.statuses, s => s.id === n.id);
        const errors = this.filterErrors([
          `statuses[${index}]`,
          `!statuses[${index}].outgoingArrows`
        ]);
        //console.log('errors', n.label, index, this.errors, errors);
        return errors && errors.length > 0;
      }
    },

    keyFor(...args) {
      return args.join("_");
    },

    getOutgoingArrows(n) {
      return n.outgoingArrows
        .map(a => ({
          label: a.label,
          fromStatus: n,
          toStatus: this.nodes.find(n => n.id === a.toStatusId)
        }))
        .filter(a => a.toStatus.type !== "FAILURE");
    },

    labelFor(n) {
      let label = n.label;
      //label += '\n[' + n.id + ']';
      let actors = n.primaryActor.map(l => this.$t("userType." + l)).join(", ");
      if (actors) {
        label += "\n[" + actors + "]";
      }
      return label;
    },

    fillFor(n) {
      switch (n.type) {
        case "INITIAL":
          return "mediumturquoise";
        case "INTERMEDIATE":
          return "gold";
        case "SUCCESS":
          return "lightgreen";
        case "FAILURE":
          return "tomato";
      }

      return null;
    },

    strokeFor(n) {
      if (!this.failureNode) {
        return null;
      }

      const canFail = n.outgoingArrows.filter(
        a => a.toStatusId === this.failureNode.id
      ).length;

      return canFail ? "red" : null;
    },

    left(n) {
      return (
        (n.colPosition - 1) * (this.nodeWidth + this.hSpace) + this.padding
      );
    },

    right(n) {
      return this.left(n) + this.nodeWidth;
    },

    middleX(n) {
      return (this.right(n) - this.left(n)) / 2 + this.left(n);
    },

    top(n) {
      return (
        (n.rowPosition - 1) * (this.nodeHeight + this.vSpace) + this.padding
      );
    },

    bottom(n) {
      return this.top(n) + this.nodeHeight;
    },

    middleY(n) {
      return (this.bottom(n) - this.top(n)) / 2 + this.top(n);
    },

    // ====================================

    onClick(evt) {
      evt.stopPropagation();

      if (evt.which != 1) {
        return; // Considera solo il left-click
      }

      // Trova il target dell'evento ed eventualmente il gruppo (g)
      // più esterno a cui il target appartiente
      const target = findTarget(
        evt.target,
        this.$el,
        node => node.nodeName === "g" && node.id !== "root-group"
      );

      // Trova il componente Vue di cui il target trovato è elemento radice
      const component = findComponent(this, c => c.$el === target);

      // Se abbiamo cliccato a vuoto, non dobbiamo fare nulla
      if (!component || component === this) {
        this.$emit("none", node);
        return;
      }

      // Sia i nodi che gli archi hanno la "key" dell'iterazione impostata
      const key = component.$vnode && component.$vnode.key;
      if (!key) {
        this.$emit("none", node);
        return;
      }

      // Per gli archi, la key ha forma <fromStatusId>_<toStatusId>
      const matches = key.match(/^(.+)_(.+)$/);
      if (matches && matches.length) {
        const fromStatusId = matches[1];
        const toStatusId = matches[2];

        const fromStatus = this.nodes.find(n => n.id === fromStatusId);
        if (!fromStatus) {
          this.$emit("none", node);
          return;
        }

        const toStatus = this.nodes.find(n => n.id === toStatusId);
        if (!toStatus) {
          this.$emit("none", node);
          return;
        }

        const arrow = fromStatus.outgoingArrows.find(
          a => a.toStatusId === toStatusId
        );
        if (!arrow) {
          this.$emit("none", node);
          return;
        }

        this.$emit("arrow", { fromStatus, toStatus, arrow });
        return;
      }

      // Se non abbiamo cliccato un arco, allora vediamo se abbiamo cliccato un nodo
      const node = this.nodes.find(n => n.id === key);
      if (node) {
        this.$emit("status", node);
        return;
      }

      this.$emit("none", node);
    }
  }
};
</script>
